import React from 'react';
import { Avatar, Chip as MuiChip, ChipProps as MuiChipProps, alpha, chipClasses, styled } from '@mui/material';
export { VipChip } from './VipChip';
export type { VipChipProps } from './VipChip';
import { CustomChipColors, customChipColors, customChipColorsKeys } from './utils/customColors';

type ChipStartAdornment =
  | {
      mode: 'avatar';
      src: string | undefined;
      fallbackText?: string;
    }
  | {
      mode: 'icon';
      icon: React.ReactElement;
    };

type ChipEndAdornment =
  | {
      mode: 'delete';
      onDelete: () => void;
    }
  | {
      mode: 'icon';
      icon: React.ReactElement;
    }
  | {
      mode: 'icon-only';
      icon: React.ReactElement;
      onDelete: () => void;
    };

// Todo: remove sx, so we standardize chip styles
export interface ChipProps extends Pick<MuiChipProps, 'clickable' | 'disabled' | 'sx' | 'onClick'> {
  /**
   * The size of the chip
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * The variant of the chip(outlined has less rounded corners and no fill)
   * @default 'filled'
   */
  variant?: 'outlined' | 'filled';
  /**
   * The label of the chip
   */
  label?: string | number;
  /**
   * The color of the chip
   * @default 'default'
   */
  color?: 'default' | 'primary' | 'error' | 'warning' | 'success' | CustomChipColors;
  /**
   * The start adornment of the chip. Can be avatar/icon.
   * This should only be used when a label is present.
   */
  startAdornment?: ChipStartAdornment;
  /**
   * The end adornment of the chip. Can be the default delete icon or a custom icon or icon-only.
   */
  endAdornment?: ChipEndAdornment;
  /**
   * The test id of the chip
   */
  'data-testid'?: string;
}

const StyledChip = styled(MuiChip, {
  shouldForwardProp: (prop) => prop !== 'hasCustomEndAdornment' && prop !== 'iconOnly',
})<{ hasCustomEndAdornment: boolean; iconOnly: boolean }>(
  ({ theme, icon, avatar, deleteIcon, hasCustomEndAdornment, iconOnly, onClick }) => ({
    backgroundColor: theme.custom.colours?.grey50,
    border: `1px solid ${theme.custom.colours?.grey200}`,
    color: theme.custom.colours?.grey700,
    fill: theme.custom.colours?.grey500,
    stroke: theme.custom.colours?.grey500,
    borderRadius: '9999px',
    fontWeight: 500,
    gap: '4px',
    // Start adornment
    [`& .${chipClasses.icon}`]: {
      margin: 0,
    },
    [`& .${chipClasses.avatar}`]: {
      margin: 0,
      width: '16px',
      height: '16px',
      fontSize: '9px',
    },
    // Label
    [`& .${chipClasses.label}`]: {
      padding: 0,
      fontWeight: 500,
      display: iconOnly ? 'none' : undefined,
    },
    // End adornment
    [`& .${chipClasses.deleteIcon}`]: {
      cursor: hasCustomEndAdornment && !onClick ? 'default' : undefined,
      // We need to set the border radius here to stop the cursor flickering when hovering over the delete icon
      borderRadius: '9999px',
      margin: 0,
      padding: hasCustomEndAdornment ? undefined : '2px',
      stroke: 'inherit', // We want the delete icon to inherit the stroke color from the color variants class of the chip
      '&:hover': {
        backgroundColor: hasCustomEndAdornment ? 'transparent' : '#E9EAEB',
        borderRadius: '9999px',
      },
    },
    // Focus styles
    '&.Mui-focusVisible': {
      // We don't want the background color to change when the chip is focused
      backgroundColor: theme.custom.colours?.grey50,
      [`& .${chipClasses.deleteIcon}`]: {
        backgroundColor: hasCustomEndAdornment ? 'transparent' : '#E9EAEB',
        borderRadius: '9999px',
        boxShadow: hasCustomEndAdornment ? undefined : `0 0 0 2px #FFF, 0 0 0 4px ${theme.palette.primary.main}`,
      },
    },
    [`&.${chipClasses.sizeSmall}`]: {
      padding: iconOnly ? 0 : '2px 8px',
      paddingLeft: icon ? '6px' : avatar ? '3px' : undefined,
      paddingRight: hasCustomEndAdornment ? '6px' : deleteIcon && !iconOnly ? '3px' : undefined,
      [`& .${chipClasses.label}`]: {
        fontSize: '12px',
        lineHeight: '18px',
      },
      [`& .${chipClasses.deleteIcon}`]: {
        padding: iconOnly ? '5px' : undefined,
      },
    },
    [`&.${chipClasses.sizeMedium}`]: {
      /**
       * When the size is medium, the height of the chip is 32px.
       * But we want the height to be 26px, to match the design.
       */
      height: '26px',
      padding: iconOnly ? 0 : '2px 10px',
      paddingLeft: icon ? '6px' : avatar ? '4px' : undefined,
      paddingRight: hasCustomEndAdornment ? '8px' : deleteIcon && !iconOnly ? '4px' : undefined,
      [`& .${chipClasses.label}`]: {
        fontSize: '14px',
        lineHeight: '20px',
      },
      [`& .${chipClasses.deleteIcon}`]: {
        padding: iconOnly ? '6px' : undefined,
      },
    },
    // Custom large size (height 28px including paddings)
    '&.custom-size-large': {
      height: '28px',
      padding: iconOnly ? 0 : '2px 12px',
      paddingLeft: icon ? '8px' : avatar ? '5px' : undefined,
      paddingRight: hasCustomEndAdornment ? '10px' : deleteIcon && !iconOnly ? '5px' : undefined,
      [`& .${chipClasses.label}`]: {
        fontSize: '15px',
        lineHeight: '22px',
      },
      [`& .${chipClasses.deleteIcon}`]: {
        padding: iconOnly ? '7px' : undefined,
      },
    },
    [`&.${chipClasses.outlined}`]: {
      borderRadius: iconOnly ? '6px' : '8px',
      border: `1px solid ${theme.custom.colours?.grey300}`,
      backgroundColor: '#FFF',
      boxShadow: '0px 1px 2px 0px var(--Colors-Effects-Shadows-shadow-xs, rgba(10, 13, 18, 0.05))',
      [`& .${chipClasses.deleteIcon}`]: {
        borderRadius: iconOnly ? '6px' : '8px',
        '&:hover': {
          borderRadius: '5px',
        },
      },
      '&.Mui-focusVisible': {
        [`& .${chipClasses.deleteIcon}`]: {
          borderRadius: '3px',
        },
      },
    },
    // Color variants
    [`&.${chipClasses.colorPrimary}`]: {
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      color: theme.palette.primary.main,
      fill: alpha(theme.palette.primary.main, 0.8),
      stroke: alpha(theme.palette.primary.main, 0.8),
      [`& .${chipClasses.deleteIcon}`]: {
        '&:hover': {
          backgroundColor: hasCustomEndAdornment ? 'transparent' : alpha(theme.palette.primary.main, 0.2),
        },
      },
      [`&.Mui-focusVisible .${chipClasses.deleteIcon}`]: {
        backgroundColor: hasCustomEndAdornment ? 'transparent' : alpha(theme.palette.primary.main, 0.2),
      },
    },
    [`&.${chipClasses.colorError}`]: {
      backgroundColor: theme.custom.colours?.error50,
      border: `1px solid ${theme.custom.colours?.error200}`,
      color: theme.custom.colours?.error700,
      fill: theme.custom.colours?.error500,
      stroke: theme.custom.colours?.error500,
      [`& .${chipClasses.deleteIcon}`]: {
        '&:hover': {
          backgroundColor: hasCustomEndAdornment ? 'transparent' : theme.custom.colours?.error200,
        },
      },
      [`&.Mui-focusVisible .${chipClasses.deleteIcon}`]: {
        backgroundColor: hasCustomEndAdornment ? 'transparent' : theme.custom.colours?.error200,
      },
    },
    [`&.${chipClasses.colorWarning}`]: {
      backgroundColor: theme.custom.colours?.warning50,
      border: `1px solid ${theme.custom.colours?.warning200}`,
      color: theme.custom.colours?.warning700,
      fill: theme.custom.colours?.warning500,
      stroke: theme.custom.colours?.warning500,
      [`& .${chipClasses.deleteIcon}`]: {
        '&:hover': {
          backgroundColor: hasCustomEndAdornment ? 'transparent' : theme.custom.colours?.warning200,
        },
      },
      [`&.Mui-focusVisible .${chipClasses.deleteIcon}`]: {
        backgroundColor: hasCustomEndAdornment ? 'transparent' : theme.custom.colours?.warning200,
      },
    },
    [`&.${chipClasses.colorSuccess}`]: {
      backgroundColor: theme.custom.colours?.success50,
      border: `1px solid ${theme.custom.colours?.success200}`,
      color: theme.custom.colours?.success700,
      fill: theme.custom.colours?.success500,
      stroke: theme.custom.colours?.success500,
      [`& .${chipClasses.deleteIcon}`]: {
        '&:hover': {
          backgroundColor: hasCustomEndAdornment ? 'transparent' : theme.custom.colours?.success200,
        },
      },
      [`&.Mui-focusVisible .${chipClasses.deleteIcon}`]: {
        backgroundColor: hasCustomEndAdornment ? 'transparent' : theme.custom.colours?.success200,
      },
    },
    // Custom colors based on Fabs designs
    [`&.custom-color-greyBlue`]: {
      backgroundColor: customChipColors.greyBlue.backgroundColor,
      border: `1px solid ${customChipColors.greyBlue.borderColor}`,
      color: customChipColors.greyBlue.color,
      fill: customChipColors.greyBlue.iconColor,
      stroke: customChipColors.greyBlue.iconColor,
      [`& .${chipClasses.deleteIcon}`]: {
        '&:hover': {
          backgroundColor: hasCustomEndAdornment ? 'transparent' : customChipColors.greyBlue.borderColor,
        },
      },
      [`&.Mui-focusVisible .${chipClasses.deleteIcon}`]: {
        backgroundColor: hasCustomEndAdornment ? 'transparent' : customChipColors.greyBlue.borderColor,
      },
    },
    [`&.custom-color-lightBlue`]: {
      backgroundColor: customChipColors.lightBlue.backgroundColor,
      border: `1px solid ${customChipColors.lightBlue.borderColor}`,
      color: customChipColors.lightBlue.color,
      fill: customChipColors.lightBlue.iconColor,
      stroke: customChipColors.lightBlue.iconColor,
      [`& .${chipClasses.deleteIcon}`]: {
        '&:hover': {
          backgroundColor: hasCustomEndAdornment ? 'transparent' : customChipColors.lightBlue.borderColor,
        },
      },
      [`&.Mui-focusVisible .${chipClasses.deleteIcon}`]: {
        backgroundColor: hasCustomEndAdornment ? 'transparent' : customChipColors.lightBlue.borderColor,
      },
    },
    [`&.custom-color-blue`]: {
      backgroundColor: customChipColors.blue.backgroundColor,
      border: `1px solid ${customChipColors.blue.borderColor}`,
      color: customChipColors.blue.color,
      fill: customChipColors.blue.iconColor,
      stroke: customChipColors.blue.iconColor,
      [`& .${chipClasses.deleteIcon}`]: {
        '&:hover': {
          backgroundColor: hasCustomEndAdornment ? 'transparent' : customChipColors.blue.borderColor,
        },
      },
      [`&.Mui-focusVisible .${chipClasses.deleteIcon}`]: {
        backgroundColor: hasCustomEndAdornment ? 'transparent' : customChipColors.blue.borderColor,
      },
    },
    [`&.custom-color-indigo`]: {
      backgroundColor: customChipColors.indigo.backgroundColor,
      border: `1px solid ${customChipColors.indigo.borderColor}`,
      color: customChipColors.indigo.color,
      fill: customChipColors.indigo.iconColor,
      stroke: customChipColors.indigo.iconColor,
      [`& .${chipClasses.deleteIcon}`]: {
        '&:hover': {
          backgroundColor: hasCustomEndAdornment ? 'transparent' : customChipColors.indigo.borderColor,
        },
      },
      [`&.Mui-focusVisible .${chipClasses.deleteIcon}`]: {
        backgroundColor: hasCustomEndAdornment ? 'transparent' : customChipColors.indigo.borderColor,
      },
    },
    [`&.custom-color-purple`]: {
      backgroundColor: customChipColors.purple.backgroundColor,
      border: `1px solid ${customChipColors.purple.borderColor}`,
      color: customChipColors.purple.color,
      fill: customChipColors.purple.iconColor,
      stroke: customChipColors.purple.iconColor,
      [`& .${chipClasses.deleteIcon}`]: {
        '&:hover': {
          backgroundColor: hasCustomEndAdornment ? 'transparent' : customChipColors.purple.borderColor,
        },
      },
      [`&.Mui-focusVisible .${chipClasses.deleteIcon}`]: {
        backgroundColor: hasCustomEndAdornment ? 'transparent' : customChipColors.purple.borderColor,
      },
    },
    [`&.custom-color-pink`]: {
      backgroundColor: customChipColors.pink.backgroundColor,
      border: `1px solid ${customChipColors.pink.borderColor}`,
      color: customChipColors.pink.color,
      fill: customChipColors.pink.iconColor,
      stroke: customChipColors.pink.iconColor,
      [`& .${chipClasses.deleteIcon}`]: {
        '&:hover': {
          backgroundColor: hasCustomEndAdornment ? 'transparent' : customChipColors.pink.borderColor,
        },
      },
      [`&.Mui-focusVisible .${chipClasses.deleteIcon}`]: {
        backgroundColor: hasCustomEndAdornment ? 'transparent' : customChipColors.pink.borderColor,
      },
    },
    [`&.custom-color-orange`]: {
      backgroundColor: customChipColors.orange.backgroundColor,
      border: `1px solid ${customChipColors.orange.borderColor}`,
      color: customChipColors.orange.color,
      fill: customChipColors.orange.iconColor,
      stroke: customChipColors.orange.iconColor,
      [`& .${chipClasses.deleteIcon}`]: {
        '&:hover': {
          backgroundColor: hasCustomEndAdornment ? 'transparent' : customChipColors.orange.borderColor,
        },
      },
      [`&.Mui-focusVisible .${chipClasses.deleteIcon}`]: {
        backgroundColor: hasCustomEndAdornment ? 'transparent' : customChipColors.orange.borderColor,
      },
    },
  }),
);

const isCustomChipColor = (color?: ChipProps['color']): color is CustomChipColors => {
  return customChipColorsKeys.includes(color || '');
};

const deleteIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M9 3L3 9M3 3L9 9" stroke="inherit" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Our custom chip component that handles displaying a chip with optional start and end adornments based on our design system.
 *
 * Note:
 * - The fill & stroke colors for the icons are determined by the color of the chip.
 * - When using an icon make sure you set the fill & stroke(of the parts of the icon you want to be the same color as the chip) to inherit.
 */
const ChipComponent = React.forwardRef<HTMLDivElement, ChipProps>(
  ({ size = 'medium', label, color, startAdornment, endAdornment, ...rest }, ref) => {
    // Map our custom "large" size to Mui's "medium" prop while adding a helper class
    const muiSize: MuiChipProps['size'] | undefined = size === 'large' ? 'medium' : size;
    const sizeClass = size === 'large' ? 'custom-size-large' : '';
    const isCustomColor = isCustomChipColor(color);

    /**
     * The endAdornment only renders if the onDelete function is provided.
     * So when our icon is display only adornment icon, we need to provide a noop function.
     */
    const onDelete =
      endAdornment?.mode === 'delete' || endAdornment?.mode === 'icon-only'
        ? endAdornment.onDelete
        : endAdornment?.mode === 'icon'
        ? (e: React.MouseEvent<HTMLDivElement>) => {
            // Event needs to be propagated to the parent(so that the chip can be clicked)
            rest?.onClick?.(e);
          }
        : undefined;

    return (
      <StyledChip
        {...rest}
        ref={ref}
        data-testid={rest['data-testid'] || 'chip'}
        label={label}
        size={muiSize}
        color={isCustomColor ? 'default' : color}
        className={`${isCustomColor ? `custom-color-${color}` : ''} ${sizeClass}`.trim()}
        avatar={
          startAdornment?.mode === 'avatar' ? (
            <Avatar src={startAdornment.src}>{startAdornment.fallbackText}</Avatar>
          ) : undefined
        }
        icon={startAdornment?.mode === 'icon' ? startAdornment.icon : undefined}
        deleteIcon={endAdornment?.mode === 'delete' ? deleteIcon : endAdornment?.icon}
        onDelete={onDelete}
        hasCustomEndAdornment={endAdornment?.mode === 'icon'}
        iconOnly={endAdornment?.mode === 'icon-only'}
      />
    );
  },
);

ChipComponent.displayName = 'Chip';

export const Chip = ChipComponent;
